'use client'
import { AuthMessage, Messages } from '@/components/auth/auth-message'
import Background from '@/components/auth/background'
import JoinForm from '@/components/auth/join'
import LoginForm from '@/components/auth/login'
import { FlashMessageContext } from '@/context/flash-message'
import { AuthView } from '@/context/auth-modal-context'
import { appendParams } from '@/util/url'
import Link from 'next/link'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useContext, useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import SegmentedControl_ from 'ui/src/components/controls/segmented-control'
import { desktop } from 'ui/src/css'
import { CloseIcon as _CloseIcon } from 'ui/src/icons/navigation'

const OuterContainer = styled.div`
    display: flex;
    height: 100vh;
    ${desktop(css`
        width: 50vw;
    `)}
`
const Container = styled.div`
    position: relative;
    display: flex;
    align-items: stretch;
    flex-direction: column;
    width: 325px;
    margin: auto;
    gap: 12px;
`

const ModalCloseButton = styled.button`
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    background: transparent;
    border: 0;
    cursor: pointer;
`

const CloseIcon = styled(_CloseIcon)<{ $alwaysShow?: boolean }>`
    align-self: flex-start;
    margin-top: 10px;
    position: absolute;
    inset: 0;
    display: block;
    ${({ $alwaysShow }) =>
        !$alwaysShow &&
        desktop(css`
            display: none;
        `)}
`
const SegmentedControl = styled(SegmentedControl_)`
    width: 325px;
    display: flex;
    align-self: center;
`

const Logo = styled.img`
    margin: 0 auto;
    ${desktop(css`
        width: 320px;
        height: 150px;
    `)}
    width: 195px;
    height: 92px;
`

type Props = {
    initialView?: AuthView
    messageId?: keyof typeof Messages
    mode?: 'modal' | 'page'
    next?: string
    onAuthSuccess?: (() => void | Promise<void>) | undefined
    onClose?: (() => void) | undefined
    onViewChange?: ((view: AuthView) => void) | undefined
}

const LoginPageContents = ({
    initialView,
    messageId,
    mode = 'page',
    next,
    onAuthSuccess,
    onClose,
    onViewChange,
}: Props) => {
    const message = messageId && Messages[messageId]
    const params = useSearchParams()
    const router = useRouter()
    const isModal = mode === 'modal'
    const routeView = usePathname() === '/login' ? 'login' : 'join'
    const [modalView, setModalView] = useState<AuthView>(initialView || routeView)
    const { showMessage } = useContext(FlashMessageContext)

    useEffect(() => {
        if (isModal) {
            setModalView(initialView || 'login')
        }
    }, [initialView, isModal])

    useEffect(() => {
        if (message) {
            showMessage({ message: <AuthMessage {...message} />, type: message.type })
        }
    }, [message, showMessage])

    const activeView = isModal ? modalView : routeView
    const isLogin = activeView === 'login'
    const resolvedNext = useMemo(() => {
        if (typeof next !== 'undefined') {
            return next
        }
        return params?.get('next') || undefined
    }, [next, params])

    const setView = (view: AuthView) => {
        if (isModal) {
            setModalView(view)
            onViewChange?.(view)
            return
        }
        const p = params ? Object.fromEntries(params.entries()) : {}
        const url = appendParams(new URL('/' + (view === 'login' ? 'login' : 'join'), location.href), p)
        router.push(url.toString())
    }

    const content = (
        <Container>
            {isModal ? (
                <ModalCloseButton
                    onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        onClose?.()
                    }}
                    type="button"
                >
                    <_CloseIcon height={20} />
                </ModalCloseButton>
            ) : (
                <Link href="/">
                    <CloseIcon height={20} />
                </Link>
            )}
            {}
            <Logo src={`/static/img/be-animated.gif`} alt="Be Animated" suppressHydrationWarning />
            <SegmentedControl
                key={`segmented-control-${isLogin ? 'login' : 'join'}-${mode}`}
                defaultIndex={isLogin ? 0 : 1}
                size="medium"
                gradient={isLogin ? 'blue-green' : 'purple-indigo'}
                options={['Log In', 'Sign Up']}
                onChange={(index) => {
                    setView(index === 0 ? 'login' : 'join')
                }}
            />
            {isLogin ? (
                <LoginForm mode={mode} next={resolvedNext} onSuccess={onAuthSuccess} />
            ) : (
                <JoinForm
                    mode={mode}
                    next={resolvedNext}
                    onSuccess={onAuthSuccess}
                    onSwitchToLogin={() => setView('login')}
                />
            )}
        </Container>
    )

    if (isModal) {
        return content
    }

    return (
        <>
            <Background />
            <OuterContainer>{content}</OuterContainer>
        </>
    )
}

export default LoginPageContents
